import { useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/api/endpoints/auth'
import {
  isRefreshableSession,
  refreshAccessToken,
  setAccessToken,
  setSessionLostHandler,
} from '@/api/client'
import type { ExamRegistration, TokenResponse } from '@/api/types'
import {
  forgetGuestSession,
  hasStoredGuestSession,
  readGuestSession,
} from '@/features/exam/guestSession'
import { useSessionStore } from './sessionStore'

/* Session bootstrap.

   On a cold load the access token is gone — it only ever lived in memory. The
   httpOnly refresh cookie is still there, so one refresh restores the session
   silently. Only if that fails is the user actually anonymous.

   A GUEST is restored first, and the ordering is deliberate. A guest token
   only exists at all while an exam window is open, so finding one means
   somebody reloaded — or crashed — mid-exam, and getting them back into the
   paper beats restoring a portal session they can reach again at any time.
   The reverse priority would eject a participant from a timed test on the
   strength of a cookie left over from another day. */

export function useSessionBootstrap(): void {
  const setUser = useSessionStore((state) => state.setUser)
  const setAnonymous = useSessionStore((state) => state.setAnonymous)
  const markGuestSessionEnded = useSessionStore((state) => state.markGuestSessionEnded)

  useEffect(() => {
    let cancelled = false

    async function bootstrap(): Promise<void> {
      /* Asked BEFORE the read, because the read clears an entry it finds
         expired. Without this, a participant who closes the tab and comes back
         after the window shut is indistinguishable from a first-time visitor,
         and gets a login form instead of being told their exam is over. */
      const hadGuest = hasStoredGuestSession()
      const guest = readGuestSession()
      if (guest !== null) {
        setAccessToken(guest.access_token, 'guest')
        try {
          const me = await authApi.me()
          if (!cancelled) setUser(me)
          return
        } catch {
          /* Revoked, expired early, or the participant was removed. Fall
             through to the account path rather than stranding a browser that
             may also have a perfectly good student session on it. */
          forgetGuestSession()
          setAccessToken(null)
          if (!cancelled) markGuestSessionEnded()
        }
      } else if (hadGuest && !cancelled) {
        // Present but past its expiry — the read has already cleared it.
        markGuestSessionEnded()
      }
      if (cancelled) return

      const token = await refreshAccessToken()
      if (cancelled) return

      if (token === null) {
        setAnonymous()
        return
      }
      try {
        const me = await authApi.me()
        if (!cancelled) setUser(me)
      } catch {
        if (!cancelled) setAnonymous()
      }
    }

    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [setUser, setAnonymous, markGuestSessionEnded])
}

/* Registering on a share link. Same two-step shape as signing in — obtain the
   token, then read the profile — so the guest block on `/auth/me` is the one
   source of the name and link context the screens render. */
export function useCompleteGuestSignIn(): (registration: ExamRegistration) => Promise<void> {
  const signInGuest = useSessionStore((state) => state.signInGuest)

  return useCallback(
    async (registration: ExamRegistration) => {
      setAccessToken(registration.access_token, 'guest')
      const me = await authApi.me()
      signInGuest(registration, me)
    },
    [signInGuest],
  )
}

/* Leaving a guest exam.

   Ends the guest session, then tries the refresh cookie: this browser may also
   have a student signed in, and a guest finishing an exam in one tab must not
   leave them apparently logged out. */
export function useEndGuestSession(): () => Promise<void> {
  const endGuestSession = useSessionStore((state) => state.endGuestSession)
  const setUser = useSessionStore((state) => state.setUser)
  const queryClient = useQueryClient()

  return useCallback(async () => {
    endGuestSession()
    queryClient.clear()

    const token = await refreshAccessToken()
    if (token === null) return
    try {
      setUser(await authApi.me())
    } catch {
      /* No account session behind the cookie after all — anonymous is already
         the state `endGuestSession` left behind. */
    }
  }, [endGuestSession, setUser, queryClient])
}

/* Signing in is two calls against this backend: obtain the token, then read
   the profile. Every sign-in path goes through here so no screen has to
   remember the second half. */
export function useCompleteSignIn(): (
  token: TokenResponse,
  audience?: 'student' | 'admin',
) => Promise<void> {
  const signIn = useSessionStore((state) => state.signIn)

  return useCallback(
    async (token: TokenResponse, audience: 'student' | 'admin' = 'student') => {
      // The token must be in place before the profile call — it is an
      // authenticated endpoint.
      setAccessToken(token.access_token)
      const me = audience === 'admin' ? await authApi.adminMe() : await authApi.me()
      signIn(token, me)
    },
    [signIn],
  )
}

/* Refresh failing terminally mid-session. The exam screen's buffer is already
   on disk, so the answers survive a re-login — this only has to stop the app
   pretending it is still signed in. */
export function useSessionLostHandler(): void {
  const setAnonymous = useSessionStore((state) => state.setAnonymous)
  const markGuestSessionEnded = useSessionStore((state) => state.markGuestSessionEnded)
  const queryClient = useQueryClient()

  useEffect(() => {
    setSessionLostHandler(() => {
      /* Which kind of session just died decides which screen the guard shows,
         and this is the last moment either is knowable — the client still
         holds the token kind here, and clears nothing until told to. A guest
         token is the non-refreshable one, so that is the test. */
      if (!isRefreshableSession()) markGuestSessionEnded()
      setAnonymous()
      queryClient.clear()
    })
    return () => setSessionLostHandler(null)
  }, [setAnonymous, markGuestSessionEnded, queryClient])
}

export function useLogout(): () => Promise<void> {
  const signOut = useSessionStore((state) => state.signOut)
  const queryClient = useQueryClient()

  return async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      // Local state is cleared whether or not the server call succeeded — a
      // failed logout must not leave the user apparently signed in.
      signOut()
      queryClient.clear()
    }
  }
}
